// KSK063 — preview mode for Minty's "Kiosk Appearance" page.
//
// Minty embeds the real kiosk in an iframe with ?ksk_preview=1. In this mode:
//  - "pick" mode (default): a tap on an element that has a colour slot
//    (data-ksk-slot, see config/kioskColorSlots.js) does not run its action;
//    the slot is posted to Minty, which opens the colour popup.
//  - "interact" mode lets taps through so the merchant can reach popups.
//  - Minty posts the colours being edited; they apply at once (not saved —
//    Minty saves them through its own API).
//  - Requests that would write anything (orders, payments, OTP, profile …)
//    are answered locally with a failure, so a preview can never place an order.
//
// Messages to Minty:   { source: "ksk-preview", type: "ready" | "select", ... }
// Messages from Minty: { target: "ksk-preview", type: "colors" | "mode" | "navigate", ... }

import api from "@/services/api";
import { KIOSK_COLOR_SLOTS, KIOSK_COLOR_PARTS } from "@/config/kioskColorSlots";

const FLAG = "KSK_PREVIEW";

const PAGES = {
  landing: "LandingPageKiosk",
  menu: "OrderPageKiosk",
  cart: "CheckoutPageKiosk",
  payment: "PaymentSelectionPageKiosk",
  rewards: "MyRewardPageKiosk",
};

const BLOCKED =
  /save-order|save-transaction|payment-intent|request-payment|process-payment|card-sale|cnp-sale|cancel|retry-order|shopback\/create|checkout-session|checkout-hpp|exchange|direct-deduct|send-otp|signup|update-profile|refund|add-direct-amount|assign-paid|request-action|close-order|update-status|delete-item|choose-number-of-guests|get-qr-code/;

// Computed colour (rgb(...) or color(srgb ...) from color-mix) → #rrggbb, or
// null when transparent.
function toHex(value) {
  const v = String(value || "");
  let rgb = null;
  let m = /rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?/.exec(v);
  if (m) {
    if (m[4] !== undefined && Number(m[4]) === 0) return null;
    rgb = [m[1], m[2], m[3]].map(Number);
  } else if ((m = /color\(srgb ([\d.]+) ([\d.]+) ([\d.]+)(?: \/ ([\d.]+))?/.exec(v))) {
    if (m[4] !== undefined && Number(m[4]) === 0) return null;
    rgb = [m[1], m[2], m[3]].map((c) => Number(c) * 255);
  }
  if (!rgb) return null;
  return "#" + rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("");
}

// What the element shows now, to prefill Minty's custom colour fields.
function currentColors(el) {
  const cs = getComputedStyle(el);
  const brand = getComputedStyle(document.documentElement).getPropertyValue("--ksk-brand").trim();
  return {
    outline: toHex(cs.borderTopColor) || brand || "#000000",
    font: toHex(cs.color) || "#000000",
    accent: toHex(cs.backgroundColor) || brand || "#ffffff",
  };
}

export function isKioskPreview() {
  if (window.parent === window) return false;
  try {
    if (new URLSearchParams(window.location.search).get("ksk_preview") === "1") {
      sessionStorage.setItem(FLAG, "1");
    }
    return sessionStorage.getItem(FLAG) === "1";
  } catch (e) {
    return false;
  }
}

export function startKioskPreview({ store, router }) {
  const html = document.documentElement;
  let pick = true;
  html.classList.add("ksk-preview", "ksk-preview-pick");

  const post = (msg) => window.parent.postMessage({ source: "ksk-preview", ...msg }, "*");

  api.interceptors.request.use((config) => {
    if (BLOCKED.test(config.url || "")) {
      config.adapter = () =>
        Promise.resolve({
          data: { result: { isSuccess: false, statusCode: 403, message: "Not available in preview" } },
          status: 200,
          statusText: "OK",
          headers: {},
          config,
        });
    }
    return config;
  });

  document.addEventListener(
    "click",
    (e) => {
      if (!pick) return;
      const el = e.target.closest && e.target.closest("[data-ksk-slot]");
      if (!el) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const slot = el.getAttribute("data-ksk-slot");
      const colors = store.state.kioskColors || {};
      post({
        type: "select",
        slot,
        label: KIOSK_COLOR_SLOTS[slot] || slot,
        colors: colors[slot] || null,
        current: currentColors(el),
      });
    },
    true
  );

  // Colours from Minty win over the saved ones App.vue loads with the branch
  // (that response can land after Minty's first message).
  let previewColors;
  store.subscribe((mutation) => {
    if (mutation.type === "kioskColors" && previewColors !== undefined && mutation.payload !== previewColors) {
      store.commit("kioskColors", previewColors);
    }
  });

  window.addEventListener("message", (e) => {
    if (e.source !== window.parent) return;
    const msg = e.data || {};
    if (msg.target !== "ksk-preview") return;
    if (msg.type === "colors") {
      previewColors = msg.colors || null;
      store.commit("kioskColors", previewColors);
    } else if (msg.type === "mode") {
      pick = !!msg.pick;
      html.classList.toggle("ksk-preview-pick", pick);
    } else if (msg.type === "navigate" && PAGES[msg.page]) {
      const { merchant_code, branch_code } = router.currentRoute.value.params;
      router.push({ name: PAGES[msg.page], params: { merchant_code, branch_code } });
    }
  });

  post({ type: "ready", slots: KIOSK_COLOR_SLOTS, parts: KIOSK_COLOR_PARTS, pages: Object.keys(PAGES) });
}
